import { useDemo } from "@/contexts/DemoContext";
import { getDemoStep } from "@/config/demoScenarios";
import ScenarioStepFullscreen from "./ScenarioStepFullscreen";
import DemoArrowAnnotation from "./DemoArrowAnnotation";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const ARCHETYPE_BY_SCENARIO = {
  scenario_1: "fonceuse",
  scenario_2: "procrastinateur",
  scenario_3: "perle",
} as const;

/**
 * Orchestrateur d'overlays pour les scénarios de démo.
 * Affiche l'overlay correspondant à `step.overlayType` ainsi qu'un
 * indicateur en bas à droite pour le débogage.
 */
export default function DemoOverlayController() {
  const { isInDemo, currentScenario, currentStep, personas, nextStep } = useDemo();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  if (!isInDemo || !currentScenario) return null;
  const step = getDemoStep(currentScenario, currentStep);
  if (!step) return null;

  // Only show scenario overlays on the route explicitly targeted by the
  // current step. A stale active scenario must not cover the /demo parcours.
  const onStepRoute =
    location.pathname === step.route || location.pathname.startsWith(`${step.route}/`);
  if (!onStepRoute) return null;

  const archetype = ARCHETYPE_BY_SCENARIO[currentScenario];
  const persona = personas[archetype];

  const fs = step.fullscreen;
  const facts = fs?.factsKey
    ? (t(fs.factsKey, { returnObjects: true }) as string[])
    : undefined;

  return (
    <>
      {step?.overlayType === "fullscreen" && step.fullscreen && (
        <ScenarioStepFullscreen
          employeePersona={{ nom: persona.nom, genre: persona.genre }}
          employeeThought={fs!.employeeThoughtKey ? t(fs!.employeeThoughtKey) : undefined}
          managerThought={t(fs!.managerThoughtKey)}
          revealLabel={t(fs!.revealLabelKey)}
          revealTitle={t(fs!.revealTitleKey)}
          ctaLabel={t(fs!.ctaLabelKey)}
          facts={Array.isArray(facts) ? facts : undefined}
          illustrationUrl={fs!.illustrationUrl}
          employeeLabel={t("demoV3.overlay.labels.employeeThought", { name: persona.nom.toUpperCase() })}
          managerLabel={t("demoV3.overlay.labels.managerThought")}
          onContinue={() => {
            // Scenario 3 step 0 ("Voir ce qui se passe vraiment") opens the
            // static demo evaluation page instead of advancing the demo
            // stepper, so the user lands on the survey + AI insights view.
            if (currentScenario === "scenario_3" && currentStep === 0) {
              navigate("/demo-evaluation-statique");
              return;
            }
            void nextStep();
          }}
        />
      )}

      {step.overlayType === "arrow" && currentScenario === "scenario_1" && (
        <DemoArrowAnnotation
          targetSelector='[data-demo-target="prepare-meeting"]'
          text={t("demoV3.overlay.scenario_1.arrowText")}
          direction="top"
        />
      )}

    </>
  );
}
